// -------------------------------------------------------------------------------------------------------
// HttpClient.ts | Cliente creado para realizar Get y Post mediante Vanilla Fetch
// -------------------------------------------------------------------------------------------------------

/**
 * --------------------------------------------------------
 * HTTP Client Types
 * --------------------------------------------------------
 */

/**
 * Opciones del HTTP Client
 */
export interface HttpClientOptions {
  base_url?: string;                  // URL base a solicitar  
  timeout?: number;                   // Tiempo de timeout en MS
  headers?: Record<string,string>;    // Cabeceras de la petición
  auth?: {
    token?: string;                   // Token de acceso
  };
}

/**
 * Resultado de un health check
 */
export interface HealthCheckResult {
  status: 'healthy' | 'unhealthy';    // Estado del health check
  message?: string;                   // Mensaje descriptivo
  details?: Record<string, any>;      // Detalles adicionales del check
}

/**
 * Configuración de petición fetch
 */
interface FetchConfig extends RequestInit {
  timeout?: number;
}

/**
 * HTTP Client
 *
 * Cliente HTTP simple para comunicarse con servicios JSON.
 */
export class HttpClient {

  private base_url: string;
  private timeout: number;
  public headers: Record<string, string>;

  constructor( options: HttpClientOptions = {} )
  {

    // Inicializamos las propiedades base
    this.base_url = options.base_url || '';
    this.timeout  = options.timeout || 30000;

    // Inicializamos las cabeceras
    this.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    };

    // Añadimos el token si nos los dan
    if( options.auth?.token )
      this.headers['Authorization'] = `Bearer ${options.auth.token}`;
  }

  /**
   * Setter de cabeceras
   * @param headers Array de cabeceras
   */
  set_headers( headers: Record<string, string> )
  {
    this.headers = { ...this.headers, ...headers };
  }

  /**
   * Método privado para construir la URL completa
   * @param url URL relativa o absoluta
   * @returns URL completa
   */
  private build_url( url: string ): string {

    // Si tenemos el protocolo, devolvemos la URL
    if( url.startsWith( 'http://' ) || url.startsWith( 'https://' ) )
      return url;
    
    const base = this.base_url.endsWith( '/' ) ? this.base_url.slice( 0, -1 ) : this.base_url;
    const path = url.startsWith( '/' ) ? url : `/${url}`;
    
    return `${base}${path}`;
  }

  /**
   * Método privado para realizar peticiones con timeout
   * @param url URL de la petición
   * @param config Configuración de fetch
   * @returns Promise con AbortController para timeout
   */
  private async process_fetch( url: string, config: FetchConfig = {} ): Promise<Response>
  {    
    // Creamos el AbortController para el timeout
    const controller = new AbortController();
    const timeout_id = setTimeout( () => controller.abort(), this.timeout);

    try
    {
      // Realizamos la petición
      const response = await fetch( url, {
        ...config,
        signal: controller.signal,
        headers: {
          ...this.headers,
          ...config.headers
        }
      } );

      // Limpiamos el timeout
      clearTimeout( timeout_id );

      // Verificamos si la respuesta es exitosa
      if( !response.ok )
        throw new Error( `HTTP Error: ${response.status} ${response.statusText}` );

      return response;
    }
    catch( error )
    {
      
      // Limpiamos el timeout
      clearTimeout( timeout_id );

      // Si es un abort, lanzamos timeout error
      if( error instanceof Error && error.name === 'AbortError' )
        throw new Error( `Request timeout after ${this.timeout}ms` );

      // Re-lanzamos el error original
      throw error;
    }
  }

  /**
   * Método privado para procesar la respuesta
   * @param response Respuesta de fetch
   * @returns Datos parseados
   */
  private async process_response<T = any>( response: Response ): Promise<T>
  { 
    let output: any;
    
    do
    {
      // Capturamos el content type de la petición
      const content_type = response.headers.get( 'content-type' ) || '';
      
      // Si es JSON, lo parseamos
      if( content_type.includes( 'application/json' ) )
      {
        output = await response.json();
        break;
      }
      
      // Si es texto, lo devolvemos como string
      if( content_type.includes( 'text/' ) )
      {
        output = await response.text() as T;
        break;
      }
      
      // Por defecto, intentamos parsear como JSON
      try
      {
        output = await response.json();
      }
      catch
      {
        output = await response.text() as T;
      }
    } while( false );

    return output;
  }

  /**
   * POST
   * @param url URL de la petición
   * @param data Datos a enviar
   * @param config Configuración adicional
   * @returns Respuesta de la petición
   */
  async post<T = any>( url: string, data?: any, config: FetchConfig = {} ): Promise<T>
  {
    // Construímos la URL a ejecutar la petición
    const full_url = this.build_url( url );
    
    // Ejecutamos la petición
    const response = await this.process_fetch( full_url, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify( data ) : undefined
    } );

    // Procesamos la respuesta devuelta por la petición fetch
    const fetch_response = await this.process_response<T>( response );
    return fetch_response;
  }

  /**
   * GET
   * @param url URL de la petición
   * @param config Configuración adicional
   * @returns Respuesta de la petición
   */
  async get<T = any>( url: string, config: FetchConfig = {} ): Promise<T>
  {    
    // Construímos la URL a enviar la petición
    const full_url = this.build_url( url );
    
    // Ejecutamos la petición
    const response = await this.process_fetch( full_url, {
      ...config,
      method: 'GET'
    } );

    // Procesamos la respuesta devuelta por la petición fetch
    const fetch_response = await this.process_response<T>( response );
    return fetch_response;
  }

  /**
   * PUT
   * @param url URL de la petición
   * @param data Datos a enviar
   * @param config Configuración adicional
   * @returns Respuesta de la petición
   */
  async put<T = any>( url: string, data?: any, config: FetchConfig = {} ): Promise<T>
  {    
    // Construímos la URL a enviar la petición
    const full_url = this.build_url( url );
    
    // Ejecutamos la petición
    const response = await this.process_fetch( full_url, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify( data ) : undefined
    } );

    // Procesamos la respuesta devuelta por la petición fetch
    const fetch_response = await this.process_response<T>( response );
    return fetch_response;
  }

  /**
   * DELETE
   * @param url URL de la petición
   * @param config Configuración adicional
   * @returns Respuesta de la petición
   */
  async delete<T = any>( url: string, config: FetchConfig = {} ): Promise<T>
  {    
    // Construímos la URL a enviar la petición
    const full_url = this.build_url( url );
    
    // Ejecutamos la petición
    const response = await this.process_fetch( full_url, {
      ...config,
      method: 'DELETE'
    } );

    // Procesamos la respuesta devuelta por la petición fetch
    const fetch_response = await this.process_response<T>( response );
    return fetch_response;
  }
}
